import { useEffect, useState } from 'react'
import { OS_SCALE_EVENT, getOsScale } from './anonymousA11y'

// Returns the current `--os-scale` value (1 by default, > 1 when iOS Dynamic
// Type is enlarged). Re-renders when the scale changes. Use this for places
// that can't pick up rem natively — e.g. Victory SVG charts that take numeric
// font sizes as user units, or libraries like ReactFitText that take a px cap.
export function useOsScale(): number {
  const [scale, setScale] = useState<number>(() => getOsScale())

  useEffect(() => {
    const sync = () => setScale(getOsScale())

    const handler = (e: Event) => {
      const next = (e as CustomEvent<number>).detail
      if (typeof next === 'number' && Number.isFinite(next) && next > 0) {
        setScale(next)
      } else {
        sync()
      }
    }
    window.addEventListener(OS_SCALE_EVENT, handler)

    // Fallback for cases where --os-scale is changed outside of `applyOsScale`
    // (e.g. devtools, or a future code path that bypasses the helper). Watch
    // the inline style attribute on <html> and re-read the computed value.
    let observer: MutationObserver | undefined
    if (typeof MutationObserver !== 'undefined' && document.documentElement) {
      observer = new MutationObserver(sync)
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] })
    }

    // Catch up on any change that happened between initial state and effect run.
    sync()

    return () => {
      window.removeEventListener(OS_SCALE_EVENT, handler)
      observer?.disconnect()
    }
  }, [])

  return scale
}
